/**
 * Ba trang nhật ký của menu "Bảng điện tử công cộng" (gói chiều lên CV276):
 *   - `/bang-tin-dien-tu/nhat-ky-ban-tin`  bản tin đã phát (DV013, `play-logs`) + xem lại tệp;
 *   - `/bang-tin-dien-tu/nhat-ky-lich`     lịch phát thiết bị đã nhận (DV014, `schedule-logs`);
 *   - `/bang-tin-dien-tu/nhat-ky-huy`      lịch phát thiết bị đã hủy (DV015, `cancel-logs`).
 *
 * Lọc địa bàn (cây) / từ khoá / khoảng ngày, đếm và phân trang ở máy chủ (mặc định 20 dòng).
 * "Xuất Excel" tải tệp .xlsx máy chủ dựng theo bộ lọc hiện tại (`…/export`). Xem lại tệp mở popup
 * như "Nghe" của MH-16: ảnh <img>, video <video controls>, âm thanh <audio controls>.
 */
import { useState, type ReactNode } from 'react';
import { dienVao } from '../ngon-ngu/vi';
import { viBangTin } from '../ngon-ngu/vi-bang-tin';
import {
  bangTinApi,
  type BoLocNhatKyBT,
  type DongNhatKyLichBT,
  type DongNhatKyPhatBT,
  type TrangDuLieu,
} from '../api/bang-tin-api';
import { thietBiApi, type NutDiaBan } from '../api/thiet-bi-api';
import { dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { BangDuLieu, SO_DONG_MAC_DINH, type CotBang } from '../thanh-phan/BangDuLieu';
import { TruongLoc } from '../thanh-phan/MauGiaoDien';
import { ChonDiaBanCay } from '../thanh-phan/ChonDiaBanCay';
import { ngayVnSangEpoch, OKhoangNgay } from './DanhSachLichPhat';
import { gioNgayVn, XUAT_TOI_DA_DONG } from './NhatKyLichPhatThietBi';
import { HopXemTep, loiThanhChuoi } from './BangTinChung';
import './danh-sach-lich-phat.css';
import './nhat-ky-lich-phat-thiet-bi.css';
import './bang-tin.css';

const tk = viBangTin.nhatKy;
const tc = viBangTin.chung;

type HamTai<T> = (tham: BoLocNhatKyBT, signal?: AbortSignal) => Promise<TrangDuLieu<T>>;
type HamXuat = (tham: BoLocNhatKyBT) => Promise<{ soDong: number; tong: number }>;
type CayDiaBan = (signal?: AbortSignal) => Promise<NutDiaBan[]>;

const cayMacDinh: CayDiaBan = (signal) => thietBiApi.cayDiaBan({}, signal);

type TrangThaiXuat =
  { loai: 'nghi' } | { loai: 'dang-xuat' } | { loai: 'xong'; thongBao: string; canhBao: boolean };

interface TrangNhatKyProps<T> {
  maManHinh: string;
  tieuDe: string;
  rongMoTa: string;
  tai: HamTai<T>;
  xuat: HamXuat;
  cayDiaBan: CayDiaBan;
  cot: (batDauStt: number, ds: T[]) => CotBang<T>[];
  /** Phần thêm (vd popup xem lại) đặt sau bảng. */
  phuLuc?: ReactNode;
}

/** Khung chung của ba trang nhật ký: hàng lọc, xuất Excel, bảng phân trang máy chủ. */
function TrangNhatKy<T extends { id: string }>(p: TrangNhatKyProps<T>): ReactNode {
  const [nguonId, datNguonId] = useState('');
  const [oTim, datOTim] = useState('');
  const [tuKhoa, datTuKhoa] = useState('');
  const [tuNgay, datTuNgay] = useState('');
  const [denNgay, datDenNgay] = useState('');
  const [trang, datTrang] = useState(1);
  const [soDong, datSoDong] = useState<number>(SO_DONG_MAC_DINH);
  const [xuat, datXuat] = useState<TrangThaiXuat>({ loai: 'nghi' });

  const cay = dungTaiDuLieu<NutDiaBan[]>({
    tai: (signal) => p.cayDiaBan(signal),
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [],
  });

  const dangLoc = nguonId !== '' || tuKhoa !== '' || tuNgay !== '' || denNgay !== '';
  const boLoc: BoLocNhatKyBT = {
    nguonId: nguonId || undefined,
    keyword: tuKhoa || undefined,
    tuThoiDiem: ngayVnSangEpoch(tuNgay),
    denThoiDiem: ngayVnSangEpoch(denNgay, true),
  };

  const kq = dungTaiDuLieu<TrangDuLieu<T>>({
    tai: (signal) => p.tai({ page: trang, pageSize: soDong, ...boLoc }, signal),
    laRong: (d) => d.items.length === 0,
    dangLoc,
    phuThuoc: [trang, soDong, nguonId, tuKhoa, tuNgay, denNgay],
  });

  const doiLoc =
    <V,>(dat: (v: V) => void) =>
    (v: V): void => {
      dat(v);
      datTrang(1);
    };

  const xoaLoc = (): void => {
    datNguonId('');
    datOTim('');
    datTuKhoa('');
    datTuNgay('');
    datDenNgay('');
    datTrang(1);
  };

  const xuatExcel = async (): Promise<void> => {
    datXuat({ loai: 'dang-xuat' });
    try {
      const { soDong: so, tong } = await p.xuat(boLoc);
      if (so === 0) return datXuat({ loai: 'xong', thongBao: tc.xuatRong, canhBao: false });
      const vuot = tong > so;
      datXuat({
        loai: 'xong',
        canhBao: vuot,
        thongBao: vuot
          ? dienVao(tc.xuatVuotGioiHan, { tong, gioiHan: XUAT_TOI_DA_DONG, so })
          : dienVao(tc.xuatXong, { so }),
      });
    } catch (e) {
      datXuat({ loai: 'xong', canhBao: true, thongBao: dienVao(tc.xuatLoi, { loi: loiThanhChuoi(e) }) });
    }
  };

  const ds = kq.duLieu?.items ?? [];
  const dangXuat = xuat.loai === 'dang-xuat';

  return (
    <section className="man-hinh dslp" data-man-hinh={p.maManHinh}>
      <header className="man-hinh__dau">
        <h1>{p.tieuDe}</h1>
      </header>

      <div className="dslp-loc" role="search">
        <TruongLoc nhan={tc.diaBan} htmlFor={`${p.maManHinh}-dia-ban`}>
          <ChonDiaBanCay
            id={`${p.maManHinh}-dia-ban`}
            className="dslp-cay"
            nhan={tc.diaBan}
            giaTri={nguonId}
            cay={cay.duLieu ?? []}
            nhanTatCa={tc.toanTinh}
            goiYTim={tc.timDiaBan}
            onDoi={doiLoc(datNguonId)}
          />
        </TruongLoc>
        <TruongLoc nhan={tc.tuKhoa} htmlFor={`${p.maManHinh}-tim`}>
          <form
            className="dslp-tim"
            onSubmit={(e) => {
              e.preventDefault();
              datTuKhoa(oTim.trim());
              datTrang(1);
            }}
          >
            <input
              id={`${p.maManHinh}-tim`}
              type="search"
              placeholder={tk.timGoiY}
              value={oTim}
              onChange={(e) => datOTim(e.target.value)}
            />
            <button type="submit" className="nut dslp-tim__nut" aria-label={tc.timKiem} title={tc.timKiem}>
              {tc.tim}
            </button>
          </form>
        </TruongLoc>
        <OKhoangNgay
          tienTo={p.maManHinh}
          tuNgay={tuNgay}
          denNgay={denNgay}
          onDoiTu={doiLoc(datTuNgay)}
          onDoiDen={doiLoc(datDenNgay)}
        />
        <button
          type="button"
          className="nut nklt-nut-excel"
          data-hanh-dong="xuat-excel"
          disabled={dangXuat}
          aria-busy={dangXuat}
          onClick={() => void xuatExcel()}
        >
          {dangXuat ? tc.dangXuat : tc.xuatExcel}
        </button>
      </div>

      {xuat.loai === 'xong' ? (
        <p
          className={`nklt-thong-bao${xuat.canhBao ? ' nklt-thong-bao--canh-bao' : ''}`}
          role="status"
          data-vung="ket-qua-xuat"
        >
          {xuat.thongBao}
        </p>
      ) : null}

      <BangDuLieu<T>
        nhan={p.tieuDe}
        cot={p.cot((trang - 1) * soDong, ds)}
        dong={ds}
        khoaDong={(d) => d.id}
        trangThai={kq.trangThai}
        loi={kq.loi}
        taiLau={kq.taiLau}
        onThuLai={kq.taiLai}
        tong={kq.duLieu?.total ?? 0}
        trang={trang}
        soDongMoiTrang={soDong}
        onDoiTrang={datTrang}
        onDoiSoDong={(s) => {
          datSoDong(s);
          datTrang(1);
        }}
        dangLoc={dangLoc}
        onXoaLoc={xoaLoc}
        moTaRong={p.rongMoTa}
      />
      {p.phuLuc}
    </section>
  );
}

/** Ô "tên in đậm + mã" dùng chung. */
function OTenMa({ ten, ma }: { ten: string | null | undefined; ma: string | null | undefined }): ReactNode {
  return (
    <>
      <strong className="nklt-ten">{ten || ma || tc.khongCo}</strong>
      {ma ? <span className="nklt-phu">{ma}</span> : null}
    </>
  );
}

/* --------------------------- DV013 — bản tin đã phát --------------------------- */

export interface ApiNhatKyPhatBT {
  nhatKy: HamTai<DongNhatKyPhatBT>;
  xuat: HamXuat;
  xemLai: (id: string, signal?: AbortSignal) => Promise<{ url: string }>;
  cayDiaBan: CayDiaBan;
}

export const API_NHAT_KY_PHAT_BT_MAC_DINH: ApiNhatKyPhatBT = {
  nhatKy: bangTinApi.nhatKyPhat,
  xuat: bangTinApi.xuatNhatKyPhat,
  xemLai: bangTinApi.xemLaiTep,
  cayDiaBan: cayMacDinh,
};

export function NhatKyBanTinDaPhatBT({
  api = API_NHAT_KY_PHAT_BT_MAC_DINH,
}: { api?: ApiNhatKyPhatBT } = {}): ReactNode {
  const [xem, datXem] = useState<DongNhatKyPhatBT | null>(null);
  const tenBanTin = (d: DongNhatKyPhatBT): string => d.tieuDe || d.banTinMa || d.id;

  return (
    <TrangNhatKy<DongNhatKyPhatBT>
      maManHinh="nhat-ky-ban-tin-bang-tin"
      tieuDe={tk.phat.tieuDe}
      rongMoTa={tk.phat.rongMoTa}
      tai={api.nhatKy}
      xuat={api.xuat}
      cayDiaBan={api.cayDiaBan}
      cot={(batDau, ds) => [
        { khoa: 'stt', nhan: tc.stt, canGiua: true, ve: (d) => batDau + ds.indexOf(d) + 1 },
        { khoa: 'banTin', nhan: tk.cot.banTin, ve: (d) => <OTenMa ten={d.tieuDe} ma={d.banTinMa} /> },
        {
          khoa: 'loai',
          nhan: tk.cot.loai,
          ve: (d) => (d.loaiBanTin ? (viBangTin.loaiBanTin[d.loaiBanTin] ?? d.loaiBanTin) : tc.khongCo),
        },
        {
          khoa: 'vung',
          nhan: tk.cot.vungPhat,
          ve: (d) => (d.vungPhat ? (viBangTin.vungPhat[d.vungPhat] ?? d.vungPhat) : tc.khongCo),
        },
        {
          khoa: 'thietBi',
          nhan: tk.cot.thietBi,
          ve: (d) => <OTenMa ten={d.tenThietBi} ma={d.bangDienTuId} />,
        },
        { khoa: 'diaBan', nhan: tk.cot.diaBan, ve: (d) => d.tenDiaBan ?? d.nguonId ?? tc.khongCo },
        {
          khoa: 'batDau',
          nhan: tk.cot.batDau,
          ve: (d) => <span className="dslp-khong-xuong">{gioNgayVn(d.thoiDiemBatDau)}</span>,
        },
        { khoa: 'thoiLuong', nhan: tk.cot.thoiLuong, canGiua: true, ve: (d) => d.thoiLuong || tc.khongCo },
        {
          khoa: 'nguon',
          nhan: tk.cot.nguon,
          ve: (d) =>
            d.nguonKichHoat ? (viBangTin.nguonKichHoat[d.nguonKichHoat] ?? d.nguonKichHoat) : tc.khongCo,
        },
        {
          khoa: 'xemLai',
          nhan: tk.cot.xemLai,
          canGiua: true,
          ve: (d) => (
            <button
              type="button"
              className="nut nut--lien-ket"
              data-hanh-dong="xem-lai"
              disabled={d.coTep === false}
              title={d.coTep === false ? tk.khongCoTep : undefined}
              aria-label={dienVao(tk.xemNhan, { ten: tenBanTin(d) })}
              onClick={() => datXem(d)}
            >
              {tk.xem}
            </button>
          ),
        },
      ]}
      phuLuc={
        xem ? (
          <HopXemTep
            tieuDe={tenBanTin(xem)}
            loaiBanTin={xem.loaiBanTin}
            layUrl={(signal) => api.xemLai(xem.id, signal)}
            thongBaoKhongCo={tk.khongCoTep}
            onDong={() => datXem(null)}
          />
        ) : null
      }
    />
  );
}

/* ------------------- DV014 / DV015 — lịch phát thiết bị nhận / hủy ------------------- */

export interface ApiNhatKyLichBT {
  nhatKy: HamTai<DongNhatKyLichBT>;
  xuat: HamXuat;
  cayDiaBan: CayDiaBan;
}

export const API_NHAT_KY_LICH_BT_MAC_DINH: ApiNhatKyLichBT = {
  nhatKy: bangTinApi.nhatKyLich,
  xuat: bangTinApi.xuatNhatKyLich,
  cayDiaBan: cayMacDinh,
};

export const API_NHAT_KY_HUY_BT_MAC_DINH: ApiNhatKyLichBT = {
  nhatKy: bangTinApi.nhatKyHuy,
  xuat: bangTinApi.xuatNhatKyHuy,
  cayDiaBan: cayMacDinh,
};

function cotLich(nhanThoiDiem: string) {
  return (batDau: number, ds: DongNhatKyLichBT[]): CotBang<DongNhatKyLichBT>[] => [
    { khoa: 'stt', nhan: tc.stt, canGiua: true, ve: (d) => batDau + ds.indexOf(d) + 1 },
    { khoa: 'lich', nhan: tk.cot.lich, ve: (d) => <OTenMa ten={d.tenLichPhat} ma={d.lichPhatMa} /> },
    { khoa: 'thietBi', nhan: tk.cot.thietBi, ve: (d) => <OTenMa ten={d.tenThietBi} ma={d.bangDienTuId} /> },
    { khoa: 'diaBan', nhan: tk.cot.diaBan, ve: (d) => d.tenDiaBan ?? d.nguonId ?? tc.khongCo },
    {
      khoa: 'thoiDiem',
      nhan: nhanThoiDiem,
      ve: (d) => <span className="dslp-khong-xuong">{gioNgayVn(d.thoiDiem)}</span>,
    },
  ];
}

export function NhatKyLichPhatBT({
  api = API_NHAT_KY_LICH_BT_MAC_DINH,
}: { api?: ApiNhatKyLichBT } = {}): ReactNode {
  return (
    <TrangNhatKy<DongNhatKyLichBT>
      maManHinh="nhat-ky-lich-bang-tin"
      tieuDe={tk.lich.tieuDe}
      rongMoTa={tk.lich.rongMoTa}
      tai={api.nhatKy}
      xuat={api.xuat}
      cayDiaBan={api.cayDiaBan}
      cot={cotLich(tk.cot.thoiDiemNhan)}
    />
  );
}

export function NhatKyHuyLichBT({
  api = API_NHAT_KY_HUY_BT_MAC_DINH,
}: { api?: ApiNhatKyLichBT } = {}): ReactNode {
  return (
    <TrangNhatKy<DongNhatKyLichBT>
      maManHinh="nhat-ky-huy-bang-tin"
      tieuDe={tk.huy.tieuDe}
      rongMoTa={tk.huy.rongMoTa}
      tai={api.nhatKy}
      xuat={api.xuat}
      cayDiaBan={api.cayDiaBan}
      cot={cotLich(tk.cot.thoiDiemHuy)}
    />
  );
}
